import { BetChangeRowCondition, BetSlipMode } from "../../types/enums";

/**
 * Generate a unique ID for widget instances
 */
export const generateUniqueId = (): string => {
  return (
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  );
};

/**
 * Build modal changes
 */
export const buildModalChanges = (
  message: AuthMessageLike,
  betSlipState: IBetSlipState,
  individualStakes: { [key: string]: number },
  globalStake: number,
  originalTotalOdds: number,
  newTotalOdds: number,
): ModalBetChange[] => {
  const betChanges = message.betChanges ?? [];
  const rowChanges = message.rowChanges ?? [];
  const selections = betSlipState.selections ?? [];
  const { betBuilderState, mode } = betSlipState;

  if (betChanges.length === 0 && rowChanges.length === 0) {
    return [];
  }

  const rowChange = rowChanges[0];
  const newStake = rowChange?.newAmount;
  const eventName = selections[0]
    ? `${selections[0].homeName || ""} - ${selections[0].awayName || ""}`.trim()
    : betBuilderState?.bets?.[0]?.eventName || "Event";

  // Combo mode: show one aggregated card with total stake and total odds
  if (mode === BetSlipMode.Combo) {
    return [
      {
        eventName,
        originalOdds: originalTotalOdds,
        newOdds: newTotalOdds,
        originalStake: globalStake,
        newStake,
        isChanged: true,
        oddsChanged: betChanges.some((bc) => bc.isChanged),
        stakeChanged: !!rowChange?.isChanged,
      },
    ];
  }

  // Single mode: show one card per changed bet
  const changes: ModalBetChange[] = [];
  betChanges.filter((bc) => bc.isChanged).forEach((bc) => {
    const selection = selections.find(
      (s) =>
        bc.selections.includes(s.eventId) &&
        s.betTypeId === bc.betTypeId &&
        (s.oddName === bc.oddName || s.oddKey === bc.oddName),
    );

    const bbBet = betBuilderState?.bets?.find(
      (b) =>
        bc.selections.includes(Number(b.eventId)) &&
        Number(b.id) === bc.betTypeId &&
        (b.selectionName === bc.oddName ||
          b.selectionName?.toLowerCase() === bc.oddName?.toLowerCase()),
    );

    const evtName = selection
      ? `${selection.homeName || ""} - ${selection.awayName || ""}`.trim() ||
        "Event"
      : bbBet?.eventName || "Event";

    const rowCh = rowChanges.find((rc) => rc.orderRowId === bc.orderRowId);
    const individualStake = selection
      ? (individualStakes[selection.key] ?? 0)
      : undefined;

    changes.push({
      eventName: evtName,
      // oldOddPrice/previouseOdds hold the price from just before this same
      // authorization response overwrote oddPrice/odds with bc.newPrice -
      // selection.oddPrice here would already equal the new price.
      originalOdds:
        selection?.oldOddPrice ||
        selection?.oddPrice ||
        bbBet?.previouseOdds ||
        bbBet?.odds ||
        0,
      newOdds: bc.newPrice,
      originalStake: individualStake,
      // If no stake change for this row, fall back to the selection's own stake (not total)
      newStake: rowCh?.newAmount ?? individualStake,
      oddName: bc.oddName,
      line: bc.line,
      betTypeId: bc.betTypeId,
      isChanged: bc.isChanged,
      oddsChanged: bc.isChanged,
      stakeChanged: !!rowCh?.isChanged,
    });
  });

  // Single mode: if only stakes changed (no betChanges), build entries from rowChanges
  // so the modal always shows per-selection odds + stakes rather than a 0.00 fallback
  if (changes.length === 0 && rowChanges.length > 0) {
    rowChanges.forEach((rc) => {
      const selection = selections.find((s) =>
        rc.selections.includes(s.eventId),
      );
      if (selection) {
        changes.push({
          eventName:
            `${selection.homeName || ""} - ${selection.awayName || ""}`.trim() ||
            "Event",
          originalOdds: selection.oddPrice,
          newOdds: selection.oddPrice, // odds unchanged
          originalStake: individualStakes[selection.key] ?? 0,
          newStake: rc.newAmount,
          isChanged: rc.isChanged,
          oddsChanged: false,
          stakeChanged: rc.isChanged,
        });
      }
    });
  }

  return changes;
};

/**
 * Determine which rows (odds/stake) should be shown for a bet change.
 * Driven directly by the API's own isChanged flags on betChanges/rowChanges,
 * not by diffing recomputed odds/stake totals - a combo bet's recomputed
 * total can silently fail to reflect a per-leg price update.
 */
export const resolveBetChangeRowCondition = (
  oddsChanged: boolean,
  stakeChanged: boolean,
): BetChangeRowCondition => {
  if (oddsChanged && stakeChanged) return BetChangeRowCondition.Both;
  if (oddsChanged) return BetChangeRowCondition.OddsOnly;
  if (stakeChanged) return BetChangeRowCondition.StakeOnly;
  return BetChangeRowCondition.None;
};

/**
 * Format a number to 2 decimal places
 */

export const formatNumber = (value: number): string =>
  value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
